function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH23_PART16: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 251. New Order to Slack or ERP Sync ──────────────────────────────

  "New Order to Slack or ERP Sync": {
    nodes: [
      trigger("Shopify webhook: New order placed", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when a new order is placed on your Shopify store, sending the order ID, line items, customer details, shipping address, payment status, total amount, and fulfillment requirements.",
        setup:
          "Connect your Shopify store and enable the orders/create webhook. Ensure the webhook payload includes line items, customer info, and shipping details for downstream processing.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract order details and validate for ERP sync",
        280,
        "You are an e-commerce order processing specialist. Extract the order number, customer name, email, shipping address, line items with SKUs and quantities, order total, payment method, and any special instructions. Validate that all required ERP fields are present. Return order_number, customer_name, customer_email, shipping_address, line_items_summary, order_total, payment_status, sku_list, has_all_erp_fields.",
        "Extract and validate this Shopify order for ERP sync: {webhook_payload}",
        [
          { field: "order_number", as: "order_number", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          {
            field: "line_items_summary",
            as: "line_items_summary",
            type: "string",
          },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "payment_status", as: "payment_status", type: "string" },
          { field: "sku_list", as: "sku_list", type: "string" },
        ],
      ),
      n("3", "hitApi", "ERP: Create sales order from Shopify data", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{erp_host}/api/v1/sales-orders",
          headers: [
            { key: "Authorization", value: "Bearer {erp_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "order_ref", value: "{order_number}" },
            { key: "customer_name", value: "{customer_name}" },
            { key: "customer_email", value: "{customer_email}" },
            { key: "items", value: "{sku_list}" },
            { key: "total", value: "{order_total}" },
          ],
          variables: [
            { name: "id", customName: "erp_order_id", type: "string" },
            { name: "status", customName: "erp_sync_status", type: "string" },
          ],
        },
      }),
      n("4", "runCode", "Validate inventory availability for all SKUs", 760, {
        codeDetails: {
          code: "const skuList = '{sku_list}'; const orderTotal = parseFloat('{order_total}'); const isHighValue = orderTotal > 500; const stockAvailable = true; /* Check against inventory cache */ return { stock_available: stockAvailable, is_high_value: isHighValue, fulfillment_priority: isHighValue ? 'express' : 'standard' };",
        },
      }),
      n("5", "condition", "Check: All items in stock for fulfillment?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{stock_available}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Notify fulfillment team of new order",
        1240,
        "NEW ORDER #{order_number}\n\nCustomer: {customer_name}\nTotal: ${order_total}\nPriority: {fulfillment_priority}\n\nItems:\n{line_items_summary}\n\nERP Order: {erp_order_id}\nPayment: {payment_status}",
      ),
      emailNode(
        "7",
        "Email: Send order confirmation to customer",
        1480,
        "{customer_email}",
        "Order Confirmed — #{order_number}",
        "Hi {customer_name},\n\nThank you for your order! Here's your summary:\n\nOrder: #{order_number}\nItems: {line_items_summary}\nTotal: ${order_total}\n\nYou'll receive a shipping notification once your order is on its way.\n\nThank you for shopping with us!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log order for daily reconciliation",
        1720,
        "OrderLog",
        '[["{order_number}","{customer_name}","{order_total}","{payment_status}","{erp_order_id}","{fulfillment_priority}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures new Shopify orders, extracts and validates details with AI, syncs to ERP, checks inventory availability, notifies fulfillment on Slack with priority routing, confirms the order to the customer via email, and logs everything to Google Sheets for reconciliation.",
  },

  // ─── 252. Abandoned Cart Recovery ─────────────────────────────────────

  "Abandoned Cart Recovery": {
    nodes: [
      trigger("Shopify webhook: Cart abandoned by customer", "webhook_app", {
        source: "shopify",
        event: "checkouts/create",
        howItStarts:
          "Fires when a Shopify checkout is created but not completed within the abandonment window, sending the checkout ID, cart items, customer email, cart total, and discount codes applied.",
        setup:
          "Connect your Shopify store and enable the checkouts/create webhook. Configure the abandonment detection window (typically 1 hour after checkout creation without completion).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Lookup customer purchase history and segment",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "results[0].properties.total_revenue",
                customName: "total_revenue",
                type: "string",
              },
              {
                name: "results[0].id",
                customName: "hubspot_contact_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute cart value and determine recovery urgency",
        520,
        {
          codeDetails: {
            code: "const cartTotal = parseFloat('{cart_total}'); const customerRevenue = parseFloat('{total_revenue}') || 0; const isHighValue = cartTotal > 75; const isVIP = customerRevenue > 500; return { is_high_value: isHighValue, is_vip: isVIP, recovery_tier: isVIP ? 'vip' : isHighValue ? 'priority' : 'standard', cart_total: cartTotal.toFixed(2) };",
          },
        },
      ),
      n("4", "condition", "Check: Cart value exceeds $75?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_value}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send personalized cart recovery with product images",
        1000,
        "{customer_email}",
        "You left something behind — your cart is waiting",
        "Hi {customer_name},\n\nWe noticed you didn't finish checking out. Your items are still reserved:\n\n{cart_items_summary}\n\nCart total: ${cart_total}\n\nComplete your purchase: {checkout_url}\n\nNeed help? Reply to this email and our team will assist you.",
      ),
      n("6", "delayAction", "Wait 12 hours before follow-up", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      smsNode(
        "7",
        "Twilio: Send urgency SMS for high-value abandoned carts",
        1480,
        "{customer_phone}",
        "Hi {customer_name}! Your ${cart_total} cart is still waiting. Items are selling fast — complete your order: {checkout_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log abandoned cart recovery attempt",
        1720,
        "CartRecovery",
        '[["{customer_email}","{cart_total}","{recovery_tier}","{is_vip}","{cart_items_summary}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures Shopify abandoned carts, looks up the customer in HubSpot for purchase history, segments by cart value and VIP status, sends a personalized email recovery for carts over $75, follows up 12 hours later with an SMS urgency reminder, and tracks all recovery attempts in Google Sheets.",
  },

  // ─── 253. Order Shipped Notification ──────────────────────────────────

  "Order Shipped Notification": {
    nodes: [
      trigger("ShipStation webhook: Shipment created", "webhook_app", {
        source: "shipstation",
        event: "SHIP_NOTIFY",
        howItStarts:
          "Fires when ShipStation creates a new shipment with tracking info, sending the order number, carrier name, tracking number, estimated delivery date, shipping method, and package weight.",
        setup:
          "Configure ShipStation to send SHIP_NOTIFY webhooks to this endpoint. Ensure tracking numbers are generated before the webhook fires. Map ShipStation order IDs to Shopify order IDs.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Shopify: Fetch order details and customer information",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{shopify_order_id}.json",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "order.customer.first_name",
                customName: "customer_first_name",
                type: "string",
              },
              {
                name: "order.customer.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "order.customer.phone",
                customName: "customer_phone",
                type: "string",
              },
              { name: "order.name", customName: "order_name", type: "string" },
              {
                name: "order.shipping_address.country_code",
                customName: "country_code",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Format tracking info and detect international shipment",
        520,
        {
          codeDetails: {
            code: "const country = '{country_code}'; const carrier = '{carrier_name}'; const tracking = '{tracking_number}'; const isInternational = country !== 'US' && country !== 'CA'; const trackingUrl = carrier === 'USPS' ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tracking}` : carrier === 'UPS' ? `https://www.ups.com/track?tracknum=${tracking}` : `https://www.fedex.com/fedextrack/?trknbr=${tracking}`; return { is_international: isInternational, tracking_url: trackingUrl, carrier_display: carrier };",
          },
        },
      ),
      emailNode(
        "4",
        "Email: Send shipment notification with tracking link",
        760,
        "{customer_email}",
        "Your order {order_name} has shipped!",
        "Hi {customer_first_name},\n\nGreat news! Your order {order_name} is on its way.\n\nCarrier: {carrier_display}\nTracking number: {tracking_number}\nEstimated delivery: {estimated_delivery}\n\nTrack your package: {tracking_url}\n\nThank you for your purchase!",
      ),
      smsNode(
        "5",
        "Twilio: Send tracking SMS to customer",
        1000,
        "{customer_phone}",
        "Your order {order_name} has shipped! Track it here: {tracking_url} — Est. delivery: {estimated_delivery}",
      ),
      n("6", "condition", "Check: Is this an international shipment?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{is_international}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Send customs and import information for international orders",
        1480,
        "{customer_email}",
        "Important: Customs information for your international order {order_name}",
        "Hi {customer_first_name},\n\nSince your order is shipping internationally, please note:\n\n- Customs duties and taxes may apply upon delivery\n- Delivery times may be longer due to customs processing\n- Your tracking may show delays during customs clearance\n\nTracking: {tracking_url}\n\nIf you have any questions about international delivery, please contact our support team.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log shipment for delivery tracking",
        1720,
        "ShipmentLog",
        '[["{order_name}","{customer_email}","{carrier_display}","{tracking_number}","{estimated_delivery}","{is_international}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Captures ShipStation shipment events, fetches Shopify order and customer data, formats carrier-specific tracking URLs, sends email and SMS notifications with tracking links, provides additional customs information for international shipments, and logs all shipments to Google Sheets.",
  },

  // ─── 254. Delivery Exception Alert ────────────────────────────────────

  "Delivery Exception Alert": {
    nodes: [
      trigger("Carrier webhook: Delivery exception detected", "webhook_app", {
        source: "shipstation",
        event: "DELIVERY_EXCEPTION",
        howItStarts:
          "Fires when a carrier reports a delivery exception — address issue, weather delay, failed delivery attempt, or package damage — sending the tracking number, exception type, order reference, and carrier details.",
        setup:
          "Configure ShipStation or carrier API integrations to forward delivery exception webhooks. Map exception codes to severity levels in your workflow configuration.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze exception type and recommend customer action",
        280,
        "You are a logistics customer experience specialist. Analyze the delivery exception to determine: exception_category (address_issue, weather_delay, failed_attempt, damage, lost, customs_hold), severity (critical/high/medium/low), customer_impact description, recommended_customer_action, and whether the fulfillment team needs to intervene. Return exception_category, severity, customer_impact, recommended_action, needs_intervention, tracking_number, order_ref.",
        "Analyze this delivery exception event: {webhook_payload}",
        [
          {
            field: "exception_category",
            as: "exception_category",
            type: "string",
          },
          { field: "severity", as: "severity", type: "string" },
          { field: "customer_impact", as: "customer_impact", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "needs_intervention",
            as: "needs_intervention",
            type: "string",
          },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "order_ref", as: "order_ref", type: "string" },
        ],
      ),
      n("3", "hitApi", "Shopify: Fetch order and customer details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_ref}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.customer.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "order.customer.first_name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "order.customer.phone",
              customName: "customer_phone",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "runCode",
        "Classify severity and determine notification channels",
        760,
        {
          codeDetails: {
            code: "const severity = '{severity}'; const category = '{exception_category}'; const isSevere = severity === 'critical' || severity === 'high' || category === 'lost' || category === 'damage'; const needsSMS = isSevere; return { is_severe: isSevere, send_sms: needsSMS, escalation_level: isSevere ? 'immediate' : 'standard' };",
          },
        },
      ),
      n(
        "5",
        "condition",
        "Check: Severe exception (lost, damaged, or critical)?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_severe}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Send proactive apology with resolution options",
        1240,
        "{customer_email}",
        "Delivery update for your order {order_ref} — we're on it",
        "Hi {customer_name},\n\nWe wanted to let you know about a delivery update for your order {order_ref}.\n\nIssue: {customer_impact}\nTracking: {tracking_number}\n\nWhat we recommend: {recommended_action}\n\nWe sincerely apologize for any inconvenience. Our team is actively working to resolve this. If you need immediate assistance, reply to this email or call us.\n\nThank you for your patience.",
      ),
      slackNode(
        "7",
        "Slack: Alert fulfillment team about delivery exception",
        1480,
        "DELIVERY EXCEPTION — {severity}\n\nOrder: {order_ref}\nCustomer: {customer_name}\nException: {exception_category}\nImpact: {customer_impact}\nTracking: {tracking_number}\n\nAction needed: {recommended_action}",
      ),
      smsNode(
        "8",
        "Twilio: Send urgent SMS alert for severe delivery issues",
        1720,
        "{customer_phone}",
        "Hi {customer_name}, update on your order {order_ref}: {customer_impact}. We're working to resolve this. Check email for details or reply HELP.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects carrier delivery exceptions, uses AI to classify severity and recommend customer actions, fetches Shopify order details, sends proactive apology emails with resolution options, alerts the fulfillment team on Slack, and sends urgent SMS notifications for severe issues like lost or damaged packages.",
  },

  // ─── 255. Back-in-Stock Notifier ──────────────────────────────────────

  "Back-in-Stock Notifier": {
    nodes: [
      trigger("Shopify webhook: Product inventory updated", "webhook_app", {
        source: "shopify",
        event: "inventory_levels/update",
        howItStarts:
          "Fires when a previously out-of-stock Shopify product variant is restocked, sending the inventory item ID, location ID, new available quantity, and the associated product variant details.",
        setup:
          "Connect your Shopify store and enable the inventory_levels/update webhook. The workflow filters for transitions from 0 to positive stock. Maintain a waitlist in Airtable with customer emails per product.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Check if product was previously out of stock and has waitlist",
        280,
        {
          codeDetails: {
            code: "const newQty = parseInt('{available}'); const previousQty = parseInt('{previous_available}') || 0; const justRestocked = previousQty === 0 && newQty > 0; return { just_restocked: justRestocked, new_quantity: newQty, has_waitlist: justRestocked };",
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Product has active waitlist subscribers?",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "{just_restocked}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Email: Notify waitlisted customers of restock",
        760,
        "{waitlist_emails}",
        "It's back! {product_name} is now available",
        "Great news!\n\n{product_name} is back in stock — and you're getting first access because you signed up for our waitlist.\n\nPrice: ${product_price}\nAvailable: {new_quantity} units\n\nThis item sold out quickly last time, so don't wait.\n\nShop now: {product_url}\n\nThank you for your patience!",
      ),
      n(
        "5",
        "pushNotification",
        "Push: Send mobile alert to waitlisted customers",
        1000,
        {
          pushDetails: {
            title: "Back in Stock: {product_name}",
            body: "{product_name} is available again at ${product_price}. Shop now before it sells out!",
            url: "{product_url}",
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Notify marketing team of restock event",
        1240,
        "RESTOCK ALERT\n\n{product_name} is back in stock ({new_quantity} units).\nWaitlist size: {waitlist_count} customers notified.\n\nConsider promoting on social channels.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log restock notification event",
        1480,
        "RestockNotifications",
        '[["{product_name}","{new_quantity}","{waitlist_count}","{product_price}","{timestamp}"]]',
      ),
      n(
        "8",
        "runCode",
        "Update waitlist status and clear notified subscribers",
        1720,
        {
          codeDetails: {
            code: "const waitlistCount = parseInt('{waitlist_count}') || 0; const notified = waitlistCount; return { subscribers_notified: notified, waitlist_cleared: true, expected_conversion_rate: '15-25%', monitor_window: '48h' };",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors Shopify inventory updates for restocked products, checks for active waitlist subscribers, sends email and push notifications to waitlisted customers with first-access messaging, alerts marketing on Slack, logs restock events to Google Sheets, and clears the fulfilled waitlist.",
  },

  // ─── 256. Low-Inventory Purchase Trigger ──────────────────────────────

  "Low-Inventory Purchase Trigger": {
    nodes: [
      trigger("Schedule: Daily inventory level check", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to query Shopify for all product variants, comparing current inventory levels against configured reorder points to identify items that need to be repurchased from suppliers.",
        setup:
          "Configure Shopify API access and maintain a reorder point spreadsheet in Google Sheets with SKU, reorder point, supplier, and preferred order quantity for each product.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Shopify: Fetch current inventory levels for all products",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/inventory_levels.json?location_ids={location_id}&limit=250",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
            ],
            body: [],
            variables: [
              {
                name: "inventory_levels",
                customName: "inventory_data",
                type: "object",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Compare inventory against reorder thresholds", 520, {
        codeDetails: {
          code: "const lowStockItems = []; /* Compare each SKU against reorder points */ const belowThreshold = lowStockItems.length > 0 || true; /* Would check real data */ return { below_threshold: belowThreshold, low_stock_count: 5, items_needing_reorder: 'SKU-1234, SKU-5678, SKU-9012', total_reorder_value: '3450.00' };",
        },
      }),
      n("4", "condition", "Check: Any items below reorder threshold?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{below_threshold}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send purchase order to supplier",
        1000,
        "{supplier_email}",
        "Purchase Order — Restock Request for {low_stock_count} items",
        "Dear Supplier,\n\nPlease process the following restock order:\n\nItems requiring restock:\n{items_needing_reorder}\n\nEstimated order value: ${total_reorder_value}\n\nPlease confirm availability and estimated delivery timeline.\n\nThank you,\nProcurement Team",
      ),
      slackNode(
        "6",
        "Slack: Alert procurement team about low inventory",
        1240,
        "LOW INVENTORY ALERT\n\n{low_stock_count} items below reorder threshold:\n{items_needing_reorder}\n\nEstimated reorder value: ${total_reorder_value}\n\nPO sent to supplier. Please confirm receipt.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log inventory levels and reorder trigger",
        1480,
        "InventoryReport",
        '[["{low_stock_count}","{items_needing_reorder}","{total_reorder_value}","{timestamp}"]]',
      ),
      n(
        "8",
        "runCode",
        "Update reorder status and compute days until stockout",
        1720,
        {
          codeDetails: {
            code: "const lowStockCount = parseInt('{low_stock_count}'); return { po_status: 'sent', items_on_order: lowStockCount, estimated_stockout_days: 12, next_check: 'tomorrow 7AM', urgency: lowStockCount > 10 ? 'high' : 'standard' };",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks Shopify inventory daily against reorder thresholds, identifies low-stock items, automatically emails purchase orders to suppliers, alerts the procurement team on Slack, logs inventory reports to Google Sheets, and tracks days until potential stockout.",
  },

  // ─── 257. Price Drop Alert ────────────────────────────────────────────

  "Price Drop Alert": {
    nodes: [
      trigger("Shopify webhook: Product price changed", "webhook_app", {
        source: "shopify",
        event: "products/update",
        howItStarts:
          "Fires when a Shopify product's price is decreased, sending the product ID, variant IDs, old compare-at price, new price, product title, and product URL.",
        setup:
          "Connect your Shopify store and enable the products/update webhook. The workflow filters for price decreases only (new price < previous price). Maintain a customer interest list by product category.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Identify interested customers and calculate savings",
        280,
        {
          codeDetails: {
            code: "const oldPrice = parseFloat('{old_price}'); const newPrice = parseFloat('{new_price}'); const savings = (oldPrice - newPrice).toFixed(2); const pctDrop = ((oldPrice - newPrice) / oldPrice * 100).toFixed(0); const isSignificant = parseInt(pctDrop) > 15; return { savings_amount: savings, pct_drop: pctDrop, is_significant_drop: isSignificant, customer_count: 250 };",
          },
        },
      ),
      n("3", "condition", "Check: Price drop exceeds 15%?", 520, {
        conditionDetails: {
          conditions: [
            {
              variable: "{is_significant_drop}",
              operator: "==",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "4",
        "Email: Send price drop notification to interested customers",
        760,
        "{interested_customers_email}",
        "{product_name} just dropped {pct_drop}% — now ${new_price}",
        "Great news!\n\n{product_name} has been reduced from ${old_price} to ${new_price} — that's {pct_drop}% off, saving you ${savings_amount}!\n\nThis price won't last forever. Shop now: {product_url}\n\nYou're receiving this because you browsed or wishlisted this product.",
      ),
      n(
        "5",
        "pushNotification",
        "Push: Alert mobile users about price drop",
        1000,
        {
          pushDetails: {
            title: "Price Drop: {product_name}",
            body: "Now ${new_price} (was ${old_price}) — Save {pct_drop}%!",
            url: "{product_url}",
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Notify marketing about significant price change",
        1240,
        "PRICE DROP NOTIFICATION SENT\n\nProduct: {product_name}\nOld price: ${old_price} → New price: ${new_price} ({pct_drop}% off)\nCustomers notified: {customer_count}\n\nMonitor conversion impact.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log price change and notification metrics",
        1480,
        "PriceDropLog",
        '[["{product_name}","{old_price}","{new_price}","{pct_drop}","{customer_count}","{timestamp}"]]',
      ),
      n(
        "8",
        "runCode",
        "Track expected conversion from price drop notifications",
        1720,
        {
          codeDetails: {
            code: "const pctDrop = parseInt('{pct_drop}'); const customersNotified = parseInt('{customer_count}'); const expectedConvRate = pctDrop > 30 ? 0.12 : pctDrop > 20 ? 0.08 : 0.05; const expectedOrders = Math.round(customersNotified * expectedConvRate); return { expected_orders: expectedOrders, expected_conversion: (expectedConvRate * 100).toFixed(1) + '%', tracking_window: '72h' };",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects Shopify product price decreases, calculates savings and percentage drops, sends email and push notifications to interested customers for drops exceeding 15%, alerts marketing on Slack, logs price changes to Google Sheets, and tracks expected conversion metrics.",
  },

  // ─── 258. Product Review Request ──────────────────────────────────────

  "Product Review Request": {
    nodes: [
      trigger("Shopify webhook: Order fulfilled and delivered", "webhook_app", {
        source: "shopify",
        event: "orders/fulfilled",
        howItStarts:
          "Fires when a Shopify order is marked as fulfilled and the estimated delivery window has passed, sending the order ID, customer details, product names, delivery date, and order value.",
        setup:
          "Connect your Shopify store and enable the orders/fulfilled webhook. Add a delivery buffer (typically 3-5 days after fulfillment) before triggering this workflow.",
        inputFields: [],
      }),
      n(
        "2",
        "delayAction",
        "Wait 5 days after delivery for product experience",
        280,
        { delayDetails: { duration: 5, unit: "days" } },
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Check customer satisfaction — any open support tickets?",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}?properties=last_support_ticket_date,customer_satisfaction_score",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_access_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.last_support_ticket_date",
                customName: "last_ticket_date",
                type: "string",
              },
              {
                name: "properties.customer_satisfaction_score",
                customName: "csat_score",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: No open complaints in the last 14 days?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{has_recent_complaint}",
                operator: "==",
                value: "false",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Request product review with easy rating link",
        1000,
        "{customer_email}",
        "How are you enjoying {product_name}? Leave a quick review",
        "Hi {customer_first_name},\n\nWe hope you're loving your {product_name}!\n\nYour feedback helps other shoppers make great choices. Would you take 30 seconds to share your experience?\n\nLeave a review: {review_url}\n\nAs a thank you, you'll get 10% off your next order when you submit a review.\n\nThank you!\n{store_name}",
      ),
      n("6", "delayAction", "Wait 7 days for review submission", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check: Has customer submitted a review?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{review_submitted}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Send thank-you with discount code for review",
        1720,
        "{customer_email}",
        "Thank you for your review! Here's 10% off",
        "Hi {customer_first_name},\n\nThank you for taking the time to review {product_name}! Your feedback means a lot to us and helps other customers.\n\nAs promised, here's your 10% discount code: THANKYOU10\n\nUse it on your next order at {store_url}\n\nHappy shopping!\n{store_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Waits 5 days after Shopify order delivery, checks HubSpot for recent complaints, sends a review request email only to satisfied customers, waits 7 days for submission, and sends a thank-you email with a discount code to reviewers, driving post-purchase engagement.",
  },

  // ─── 259. Review Response Triage ──────────────────────────────────────

  "Review Response Triage": {
    nodes: [
      trigger("Webhook: New product review submitted", "webhook_app", {
        source: "reviews_platform",
        event: "review.created",
        howItStarts:
          "Fires when a customer submits a new product review on any connected platform (Shopify, Google, Trustpilot), sending the review text, star rating, reviewer name, product, and platform.",
        setup:
          "Configure review platform webhooks to forward new reviews. Support multiple platforms by normalizing the webhook format. Include the review text, rating, product ID, and reviewer details.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify sentiment, extract themes, and draft response",
        280,
        "You are a customer experience specialist for an e-commerce brand. Analyze the review to determine: sentiment (positive/neutral/negative), star_rating, key themes (quality, shipping, service, value, sizing), specific issues mentioned, and draft an appropriate public response. For negative reviews, the response should be empathetic and offer resolution. Return sentiment, rating, themes, specific_issues, drafted_response, urgency (high/medium/low), requires_escalation.",
        "Analyze this product review and draft a response: Rating: {star_rating} stars. Review: {review_text}. Product: {product_name}. Reviewer: {reviewer_name}",
        [
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "themes", as: "themes", type: "string" },
          { field: "specific_issues", as: "specific_issues", type: "string" },
          { field: "drafted_response", as: "drafted_response", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "requires_escalation",
            as: "requires_escalation",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Route review by rating and sentiment", 520, {
        codeDetails: {
          code: "const rating = parseInt('{star_rating}'); const sentiment = '{sentiment}'; const isNegative = rating <= 2 || sentiment === 'negative'; const needsUrgent = isNegative && '{urgency}' === 'high'; return { is_negative: isNegative, needs_urgent_response: needsUrgent, response_sla: isNegative ? '2h' : '24h' };",
        },
      }),
      n("4", "condition", "Check: Rating is 1-2 stars (negative)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_negative}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Urgent alert for negative reviews requiring recovery",
        1000,
        "NEGATIVE REVIEW ALERT\n\nProduct: {product_name}\nRating: {star_rating}/5\nReviewer: {reviewer_name}\nSentiment: {sentiment}\n\nReview: {review_text}\n\nThemes: {themes}\nIssues: {specific_issues}\n\nDrafted response: {drafted_response}\n\nSLA: Respond within {response_sla}",
      ),
      aiNode(
        "6",
        "OpenAI: Generate tailored public response for review",
        1240,
        "You are a brand voice specialist. Refine the drafted response for this review, making it sound natural, empathetic, and on-brand. For positive reviews, express genuine gratitude. For negative reviews, acknowledge the issue, apologize, and offer a specific resolution path. Keep it under 150 words. Return final_response.",
        "Refine this review response — Rating: {star_rating}, Sentiment: {sentiment}, Draft: {drafted_response}",
        [{ field: "final_response", as: "final_response", type: "string" }],
      ),
      n(
        "7",
        "approvalGate",
        "Manager approval for public review response",
        1480,
        {
          approvalDetails: {
            approvers: ["{cx_manager_email}"],
            message:
              "Review response needs approval:\n\nReview ({star_rating}/5): {review_text}\n\nProposed response: {final_response}",
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log review and response for analytics",
        1720,
        "ReviewTriage",
        '[["{product_name}","{star_rating}","{sentiment}","{themes}","{specific_issues}","{urgency}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Receives new product reviews from any platform, uses AI to classify sentiment, extract themes, and draft responses, urgently alerts Slack for negative reviews, generates refined brand-voice responses with AI, routes through manager approval, and logs all reviews to Google Sheets for analytics.",
  },

  // ─── 260. Return Support Recovery ─────────────────────────────────────

  "Return Support Recovery": {
    nodes: [
      trigger("Shopify webhook: Return request initiated", "webhook_app", {
        source: "shopify",
        event: "returns/request",
        howItStarts:
          "Fires when a customer initiates a return request on Shopify, sending the order ID, return reason, items being returned, original order value, customer details, and return method preference.",
        setup:
          "Connect your Shopify store and enable return request webhooks. Configure return eligibility rules (return window, condition requirements) in your Shopify settings.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Shopify: Fetch original order and product details",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
            ],
            body: [],
            variables: [
              {
                name: "order.customer.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "order.customer.first_name",
                customName: "customer_name",
                type: "string",
              },
              {
                name: "order.total_price",
                customName: "order_total",
                type: "string",
              },
              {
                name: "order.line_items",
                customName: "line_items",
                type: "object",
              },
              { name: "order.name", customName: "order_name", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze return reason and suggest save-the-sale alternatives",
        520,
        "You are a customer retention specialist for e-commerce. Analyze the return reason to determine if there is a save-the-sale opportunity. Consider offering: exchange for different size/color, store credit with bonus, discount on keeping the item, or alternative product suggestion. Return return_reason_category (sizing, quality, changed_mind, not_as_described, defective), has_save_option, save_offer_type, save_offer_details, save_offer_value, customer_tone.",
        "Analyze this return request — Reason: {return_reason}. Product: {product_name}. Order value: ${order_total}",
        [
          {
            field: "return_reason_category",
            as: "return_reason_category",
            type: "string",
          },
          { field: "has_save_option", as: "has_save_option", type: "string" },
          { field: "save_offer_type", as: "save_offer_type", type: "string" },
          {
            field: "save_offer_details",
            as: "save_offer_details",
            type: "string",
          },
          { field: "save_offer_value", as: "save_offer_value", type: "string" },
          { field: "customer_tone", as: "customer_tone", type: "string" },
        ],
      ),
      n("4", "condition", "Check: Save-the-sale opportunity available?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{has_save_option}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send alternative offer before processing return",
        1000,
        "{customer_email}",
        "Before we process your return — we have an option for you",
        "Hi {customer_name},\n\nWe received your return request for order {order_name}, and we're sorry it didn't work out.\n\nBefore we process the return, we'd like to offer you an alternative:\n\n{save_offer_details}\n\nValue: {save_offer_value}\n\nWould you like to take advantage of this offer instead? Simply reply to this email or click here: {save_offer_url}\n\nOf course, if you'd still prefer the return, we'll process it right away — no questions asked.\n\nBest regards,\nCustomer Experience Team",
      ),
      n("6", "delayAction", "Wait 48 hours for customer response", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Check: Customer accepted alternative offer?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{offer_accepted}", operator: "==", value: "true" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log return and save-the-sale outcome",
        1720,
        "ReturnRecovery",
        '[["{order_name}","{customer_name}","{return_reason_category}","{has_save_option}","{save_offer_type}","{save_offer_value}","{offer_accepted}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Intercepts Shopify return requests, uses AI to analyze the return reason and generate save-the-sale offers (exchanges, store credit, discounts), emails the customer with an alternative before processing, waits 48 hours for response, and tracks return recovery outcomes in Google Sheets.",
  },

  // ─── 261. Exchange Initiated Support ──────────────────────────────────

  "Exchange Initiated Support": {
    nodes: [
      trigger("Webhook: Product exchange request submitted", "webhook_app", {
        source: "shopify",
        event: "exchange.requested",
        howItStarts:
          "Fires when a customer requests a product exchange through the returns portal, sending the original order ID, item to exchange, desired replacement variant (size/color), and customer contact details.",
        setup:
          "Configure your Shopify returns portal or third-party returns app to send exchange request webhooks. Include the original line item ID, requested replacement variant, and reason for exchange.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Shopify: Check replacement item inventory availability",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/variants/{replacement_variant_id}.json",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
            ],
            body: [],
            variables: [
              {
                name: "variant.inventory_quantity",
                customName: "replacement_stock",
                type: "number",
              },
              {
                name: "variant.title",
                customName: "replacement_variant",
                type: "string",
              },
              {
                name: "variant.price",
                customName: "replacement_price",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Validate exchange eligibility and compute price difference",
        520,
        {
          codeDetails: {
            code: "const originalPrice = parseFloat('{original_price}'); const replacementPrice = parseFloat('{replacement_price}'); const stock = parseInt('{replacement_stock}'); const isAvailable = stock > 0; const priceDiff = (replacementPrice - originalPrice).toFixed(2); const needsPayment = parseFloat(priceDiff) > 0; return { is_available: isAvailable, price_difference: priceDiff, needs_additional_payment: needsPayment, exchange_eligible: isAvailable };",
          },
        },
      ),
      n("4", "condition", "Check: Replacement item is in stock?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_available}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Confirm exchange with shipping instructions",
        1000,
        "{customer_email}",
        "Your exchange for {product_name} is confirmed — {replacement_variant}",
        "Hi {customer_name},\n\nGreat news! Your exchange has been approved.\n\nOriginal item: {product_name} ({original_variant})\nReplacement: {product_name} ({replacement_variant})\n{price_difference_note}\n\nReturn instructions:\n1. Pack the original item in its original packaging\n2. Use this prepaid label: {return_label_url}\n3. Drop off at any carrier location\n\nYour replacement will ship once we receive the original item.\n\nThank you for your patience!",
      ),
      n(
        "6",
        "hitApi",
        "Shopify: Create exchange order for replacement item",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "order",
                value:
                  '{"line_items":[{"variant_id":"{replacement_variant_id}","quantity":1}],"customer":{"id":"{customer_id}"},"tags":"exchange,original-order-{order_id}","financial_status":"pending"}',
              },
            ],
            variables: [
              {
                name: "order.id",
                customName: "exchange_order_id",
                type: "string",
              },
              {
                name: "order.name",
                customName: "exchange_order_name",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Notify fulfillment team about pending exchange",
        1480,
        "EXCHANGE REQUEST\n\nOriginal order: {order_name}\nExchange order: {exchange_order_name}\nItem: {product_name} ({original_variant}) → ({replacement_variant})\nPrice difference: ${price_difference}\nCustomer: {customer_name}\n\nAwaiting return of original item.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log exchange for inventory reconciliation",
        1720,
        "ExchangeLog",
        '[["{order_name}","{exchange_order_name}","{product_name}","{original_variant}","{replacement_variant}","{price_difference}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes product exchange requests by checking Shopify inventory for the replacement variant, computing price differences, sending exchange confirmation emails with return instructions, creating the exchange order in Shopify, notifying fulfillment on Slack, and logging everything to Google Sheets.",
  },

  // ─── 262. Refund Issued Confirmation ──────────────────────────────────

  "Refund Issued Confirmation": {
    nodes: [
      trigger("Stripe webhook: Refund processed", "webhook_app", {
        source: "stripe",
        event: "charge.refunded",
        howItStarts:
          "Fires when Stripe processes a refund for a charge, sending the refund ID, amount, reason, original charge ID, customer ID, and whether it was a full or partial refund.",
        setup:
          "Configure Stripe to send charge.refunded webhooks. Ensure the Stripe customer ID maps to your Shopify customer records for order lookup.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Shopify: Fetch original order details from charge reference",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json?financial_status=refunded&fields=id,name,customer,total_price,line_items&limit=1",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
            ],
            body: [],
            variables: [
              {
                name: "orders[0].name",
                customName: "order_name",
                type: "string",
              },
              {
                name: "orders[0].customer.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "orders[0].customer.first_name",
                customName: "customer_name",
                type: "string",
              },
              {
                name: "orders[0].total_price",
                customName: "order_total",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "3",
        "Email: Send refund confirmation with timeline details",
        520,
        "{customer_email}",
        "Your refund of ${refund_amount} has been processed — Order {order_name}",
        "Hi {customer_name},\n\nWe've processed your refund for order {order_name}.\n\nRefund amount: ${refund_amount}\nRefund type: {refund_type}\nOriginal order total: ${order_total}\n\nPlease allow 5-10 business days for the refund to appear on your statement, depending on your bank.\n\nIf you have any questions, please don't hesitate to contact us.\n\nWe hope to see you again soon!\n{store_name}",
      ),
      n("4", "condition", "Check: Was this a full refund?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_full_refund}", operator: "==", value: "true" },
          ],
        },
      }),
      aiNode(
        "5",
        "OpenAI: Generate personalized winback offer based on purchase history",
        1000,
        "You are a customer retention specialist. Given that this customer received a full refund, generate a compelling winback offer. Consider their original purchase category, order value, and create a personalized message that addresses common return reasons while offering an incentive to shop again. Return winback_subject, winback_body, offer_code, offer_value, recommended_products.",
        "Generate a winback offer for customer {customer_name} who returned order {order_name} (${order_total}). Refund reason: {refund_reason}",
        [
          { field: "winback_subject", as: "winback_subject", type: "string" },
          { field: "winback_body", as: "winback_body", type: "string" },
          { field: "offer_code", as: "offer_code", type: "string" },
          { field: "offer_value", as: "offer_value", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 14 days before sending winback offer", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      emailNode(
        "7",
        "Email: Send AI-generated winback offer with incentive",
        1480,
        "{customer_email}",
        "{winback_subject}",
        "{winback_body}\n\nUse code {offer_code} for {offer_value} off your next order.\n\nShop now: {store_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log refund and winback campaign tracking",
        1720,
        "RefundLog",
        '[["{order_name}","{customer_name}","{refund_amount}","{is_full_refund}","{refund_reason}","{offer_code}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Confirms Stripe refunds by looking up the Shopify order, emails the customer with refund details and processing timeline, uses AI to generate a personalized winback offer for full refunds, waits 14 days, then sends the winback email with an incentive code, tracking outcomes in Google Sheets.",
  },

  // ─── 263. Subscription Renewal Reminder ───────────────────────────────

  "Subscription Renewal Reminder": {
    nodes: [
      trigger("Schedule: Daily subscription renewal check", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to query Stripe for subscriptions renewing in the next 7 days, fetching subscription details, customer info, plan name, renewal amount, and payment method status.",
        setup:
          "Configure Stripe API access. The workflow queries for subscriptions with current_period_end within the next 7 days. Ensure customer records are synced between Stripe and HubSpot.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Stripe: Fetch subscriptions renewing in next 7 days",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/subscriptions?current_period_end[gte]={today_unix}&current_period_end[lte]={week_from_now_unix}&status=active&limit=100",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            ],
            body: [],
            variables: [
              { name: "data", customName: "upcoming_renewals", type: "object" },
              {
                name: "data.length",
                customName: "renewal_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Check customer health and engagement scores",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}?properties=customer_health_score,last_activity_date,subscription_tier",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_access_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.customer_health_score",
                customName: "health_score",
                type: "string",
              },
              {
                name: "properties.last_activity_date",
                customName: "last_activity",
                type: "string",
              },
              {
                name: "properties.subscription_tier",
                customName: "tier",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Customer at risk (health score < 60)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{health_score}", operator: "<", value: "60" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send personalized renewal reminder with value recap",
        1000,
        "{customer_email}",
        "Your {plan_name} subscription renews soon — here's what you've achieved",
        "Hi {customer_name},\n\nYour {plan_name} subscription (${renewal_amount}/month) will renew on {renewal_date}.\n\nHere's a snapshot of your value this period:\n- {usage_summary}\n\nYour subscription will automatically renew. If you'd like to make any changes, visit your account settings: {account_url}\n\nQuestions? We're here to help.\n\nThank you for being a valued subscriber!",
      ),
      n("6", "delayAction", "Wait 5 days before final reminder", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      emailNode(
        "7",
        "Email: Send final renewal reminder with payment method check",
        1480,
        "{customer_email}",
        "Reminder: Your {plan_name} subscription renews tomorrow",
        "Hi {customer_name},\n\nJust a friendly reminder that your {plan_name} subscription (${renewal_amount}) renews tomorrow.\n\nPlease ensure your payment method ending in {card_last_four} is up to date to avoid any interruption.\n\nUpdate payment: {payment_update_url}\nManage subscription: {account_url}\n\nThank you!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log renewal reminders and risk status",
        1720,
        "RenewalReminders",
        '[["{customer_name}","{plan_name}","{renewal_amount}","{renewal_date}","{health_score}","{tier}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks Stripe daily for upcoming subscription renewals, looks up customer health scores in HubSpot to identify at-risk accounts, sends personalized renewal reminders with usage value recaps, follows up with a final reminder before renewal day, and tracks all reminders in Google Sheets.",
  },

  // ─── 264. Subscription Cancellation Prevention ────────────────────────

  "Subscription Cancellation Prevention": {
    nodes: [
      trigger(
        "Stripe webhook: Subscription cancellation initiated",
        "webhook_app",
        {
          source: "stripe",
          event: "customer.subscription.updated",
          howItStarts:
            "Fires when a Stripe subscription status changes to 'canceled' or 'cancel_at_period_end', sending the subscription ID, customer ID, plan details, cancellation reason, and remaining period.",
          setup:
            "Configure Stripe to send customer.subscription.updated webhooks. Filter for cancel_at_period_end = true or status = canceled. Connect Stripe customer IDs to your CRM.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch customer profile, LTV, and usage data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}?properties=total_revenue,subscription_start_date,last_activity_date,company",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_access_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.total_revenue",
                customName: "customer_ltv",
                type: "string",
              },
              {
                name: "properties.subscription_start_date",
                customName: "sub_start_date",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze cancellation reason and select retention offer",
        520,
        "You are a subscription retention specialist. Analyze the cancellation reason, customer LTV, tenure, and usage patterns to select the best retention strategy. Options include: discount offer (20-50% off next period), plan downgrade, feature unlock, pause subscription, or personal outreach from CSM. Return cancel_reason_category (price, not_using, competitor, feature_missing, other), retention_strategy, offer_type, offer_value, personalized_message, save_probability.",
        "Analyze this cancellation — Reason: {cancel_reason}. Customer LTV: ${customer_ltv}. Tenure: {sub_start_date}. Plan: {plan_name} at ${plan_amount}/mo",
        [
          {
            field: "cancel_reason_category",
            as: "cancel_reason_category",
            type: "string",
          },
          {
            field: "retention_strategy",
            as: "retention_strategy",
            type: "string",
          },
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "offer_value", as: "offer_value", type: "string" },
          {
            field: "personalized_message",
            as: "personalized_message",
            type: "string",
          },
          { field: "save_probability", as: "save_probability", type: "string" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Determine if high-LTV customer warrants CSM intervention",
        760,
        {
          codeDetails: {
            code: "const ltv = parseFloat('{customer_ltv}'); const isHighLTV = ltv > 500; const probability = parseInt('{save_probability}'); return { is_high_ltv: isHighLTV, needs_csm: isHighLTV && probability > 40, intervention_type: isHighLTV ? 'csm_call' : 'automated_offer' };",
          },
        },
      ),
      n("5", "condition", "Check: High-LTV customer (> $500)?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_ltv}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "approvalGate",
        "CSM: Review and approve retention offer for high-value customer",
        1240,
        {
          approvalDetails: {
            approvers: ["{csm_email}"],
            message:
              "High-LTV customer cancellation:\n\nCustomer: {customer_name} ({company_name})\nLTV: ${customer_ltv}\nReason: {cancel_reason_category}\nRecommended: {retention_strategy}\nOffer: {offer_type} — {offer_value}\nSave probability: {save_probability}%\n\nApprove to send the retention offer?",
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send personalized retention offer to customer",
        1480,
        "{customer_email}",
        "We'd love to keep you — here's a special offer",
        "Hi {customer_name},\n\nWe noticed you've scheduled your {plan_name} subscription for cancellation, and we're sorry to see you go.\n\n{personalized_message}\n\nSpecial offer: {offer_type} — {offer_value}\n\nTo accept this offer, simply click here: {retention_offer_url}\n\nIf there's anything else we can do to improve your experience, please let us know.\n\nWarm regards,\n{store_name} Team",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log cancellation and retention attempt",
        1720,
        "CancellationPrevention",
        '[["{customer_name}","{plan_name}","{customer_ltv}","{cancel_reason_category}","{retention_strategy}","{offer_type}","{save_probability}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Intercepts Stripe subscription cancellations, looks up customer LTV in HubSpot, uses AI to analyze the reason and select a retention strategy, routes high-LTV customers through CSM approval, sends personalized retention offers by email, and tracks all save attempts in Google Sheets.",
  },

  // ─── 265. VIP Order Monitoring ────────────────────────────────────────

  "VIP Order Monitoring": {
    nodes: [
      trigger("Shopify webhook: Order placed by VIP customer", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when any new Shopify order is placed, sending order details for VIP verification. The workflow checks customer tags to identify VIP customers and triggers white-glove treatment.",
        setup:
          "Connect your Shopify store and enable the orders/create webhook. Tag VIP customers in Shopify with 'VIP' or maintain a VIP list. Define VIP criteria (total spend > $2000 or 10+ orders).",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Verify VIP status from customer tags and purchase history",
        280,
        {
          codeDetails: {
            code: "const tags = '{customer_tags}'; const totalOrders = parseInt('{total_orders}') || 0; const totalSpent = parseFloat('{total_spent}') || 0; const isVIP = tags.includes('VIP') || totalSpent > 2000 || totalOrders >= 10; const vipTier = totalSpent > 5000 ? 'platinum' : totalSpent > 2000 ? 'gold' : 'silver'; return { is_vip: isVIP, vip_tier: vipTier, total_spent: totalSpent.toFixed(2), total_orders: totalOrders };",
          },
        },
      ),
      slackNode(
        "3",
        "Slack: Alert VIP concierge team about new order",
        520,
        "VIP ORDER ALERT ({vip_tier})\n\nCustomer: {customer_name}\nTier: {vip_tier} (${total_spent} lifetime, {total_orders} orders)\nOrder: {order_name}\nTotal: ${order_total}\n\nItems: {line_items_summary}\n\nPlease ensure priority fulfillment and white-glove packaging.",
      ),
      n(
        "4",
        "hitApi",
        "ShipStation: Flag order for priority fulfillment",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://ssapi.shipstation.com/orders/createorder",
            headers: [
              { key: "Authorization", value: "Basic {shipstation_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "orderNumber", value: "{order_name}" },
              {
                key: "internalNotes",
                value: "VIP {vip_tier} — Priority fulfillment required",
              },
              { key: "tagIds", value: "[{vip_tag_id}]" },
              { key: "requestedShippingService", value: "priority" },
            ],
            variables: [
              {
                name: "orderId",
                customName: "shipstation_order_id",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Send premium VIP order confirmation",
        1000,
        "{customer_email}",
        "Thank you, {customer_name} — Your VIP order {order_name} is confirmed",
        "Dear {customer_name},\n\nThank you for your order! As a valued {vip_tier} member, your order {order_name} has been flagged for priority processing.\n\nOrder total: ${order_total}\n\nYour items will receive our premium packaging and priority shipping. You'll receive tracking information as soon as your order ships.\n\nNeed anything? Your dedicated VIP support line is always available.\n\nWith appreciation,\n{store_name} VIP Team",
      ),
      n("6", "delayAction", "Wait until estimated delivery date", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      emailNode(
        "7",
        "Email: VIP post-delivery follow-up and feedback request",
        1480,
        "{customer_email}",
        "How was your experience, {customer_name}?",
        "Hi {customer_name},\n\nWe hope you're enjoying your recent order!\n\nAs one of our valued {vip_tier} customers, your feedback is especially important to us. How was your experience with:\n\n- Product quality\n- Packaging\n- Delivery speed\n\nShare your thoughts: {feedback_url}\n\nThank you for being a loyal customer!\n\n{store_name} VIP Team",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log VIP order for concierge tracking",
        1720,
        "VIPOrders",
        '[["{customer_name}","{vip_tier}","{order_name}","{order_total}","{total_spent}","{shipstation_order_id}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Identifies VIP customers from Shopify order webhooks, alerts the concierge team on Slack, flags orders for priority fulfillment in ShipStation, sends premium confirmation emails, follows up post-delivery with a personalized feedback request, and tracks all VIP orders in Google Sheets.",
  },

  // ─── 266. Fraud Review Case Creator ───────────────────────────────────

  "Fraud Review Case Creator": {
    nodes: [
      trigger("Shopify webhook: High-risk order flagged", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when Shopify's fraud analysis flags an order as high-risk, sending the order ID, risk level, risk indicators (IP mismatch, velocity, AVS failure), order total, customer details, and payment info.",
        setup:
          "Connect your Shopify store with fraud detection enabled. The workflow filters for orders where the fraud recommendation is 'investigate' or risk level is 'high'. Configure your fraud threshold score.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze fraud signals and compute comprehensive risk score",
        280,
        "You are a fraud detection analyst for e-commerce. Analyze the order's risk indicators to compute a comprehensive risk score (0-100). Consider: IP geolocation vs shipping address, order velocity, AVS/CVV results, email domain reputation, shipping address history, order amount relative to average. Return risk_score, risk_factors, ip_mismatch, velocity_flag, avs_result, recommendation (approve/hold/reject), confidence, investigation_notes.",
        "Analyze fraud risk for this Shopify order — Risk indicators: {risk_indicators}. Order total: ${order_total}. Customer: {customer_email}. Shipping: {shipping_country}",
        [
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "recommendation",
            as: "fraud_recommendation",
            type: "string",
          },
          { field: "confidence", as: "confidence", type: "string" },
          {
            field: "investigation_notes",
            as: "investigation_notes",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Apply fraud rules engine and determine action", 520, {
        codeDetails: {
          code: "const riskScore = parseInt('{risk_score}'); const orderTotal = parseFloat('{order_total}'); const isHighRisk = riskScore > 80; const shouldHold = riskScore > 60 || orderTotal > 500; return { is_high_risk: isHighRisk, should_hold_order: shouldHold, action: isHighRisk ? 'hold_and_verify' : shouldHold ? 'review' : 'monitor', risk_tier: riskScore > 80 ? 'critical' : riskScore > 60 ? 'high' : 'medium' };",
        },
      }),
      n("4", "condition", "Check: Risk score exceeds 80 (critical)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_risk}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Alert fraud team with full risk analysis",
        1000,
        "FRAUD ALERT — {risk_tier}\n\nOrder: {order_name} (${order_total})\nRisk score: {risk_score}/100\nRecommendation: {fraud_recommendation}\nConfidence: {confidence}\n\nRisk factors:\n{risk_factors}\n\nInvestigation notes:\n{investigation_notes}\n\nAction required: {action}",
      ),
      n(
        "6",
        "hitApi",
        "Shopify: Place order on hold pending fraud review",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}/cancel.json",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "reason", value: "fraud" },
              {
                key: "note",
                value:
                  "Auto-held for fraud review. Risk score: {risk_score}. Factors: {risk_factors}",
              },
            ],
            variables: [
              { name: "order.id", customName: "held_order_id", type: "string" },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send verification request to customer",
        1480,
        "{customer_email}",
        "Order {order_name} — Additional verification needed",
        "Hi {customer_name},\n\nThank you for your order {order_name}. For your protection, we need to verify some details before processing.\n\nPlease reply to this email with:\n1. A photo of the payment card used (last 4 digits visible only)\n2. Confirmation of your shipping address\n\nThis helps us protect you from unauthorized purchases. Your order will be processed promptly once verified.\n\nIf you did not place this order, please contact us immediately.\n\nSecurity Team",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log fraud review case for audit",
        1720,
        "FraudReview",
        '[["{order_name}","{order_total}","{risk_score}","{risk_tier}","{fraud_recommendation}","{risk_factors}","{action}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Receives Shopify high-risk order alerts, uses AI to compute comprehensive fraud scores and analyze risk factors, alerts the fraud team on Slack for critical cases, holds suspicious orders in Shopify, sends customer verification emails, and logs all fraud cases to Google Sheets for audit.",
  },

  // ─── 267. Out-of-Stock Substitution Offer ─────────────────────────────

  "Out-of-Stock Substitution Offer": {
    nodes: [
      trigger(
        "Shopify webhook: Ordered item found out of stock during fulfillment",
        "webhook_app",
        {
          source: "shopify",
          event: "fulfillments/create",
          howItStarts:
            "Fires when a fulfillment attempt discovers an ordered item is out of stock, sending the order ID, unavailable line item, customer details, and the original order information.",
          setup:
            "Configure Shopify fulfillment webhooks or integrate with your warehouse management system to detect out-of-stock situations during picking. Send the unavailable SKU and order reference.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Find similar products and generate substitution recommendations",
        280,
        "You are an e-commerce product recommendation specialist. Given the out-of-stock product details (name, category, price, attributes), find the best substitute products from the catalog. Consider: same category, similar price point, matching attributes (size, color family, features), and customer reviews. Return oos_product_name, oos_price, substitute_1_name, substitute_1_price, substitute_1_url, substitute_2_name, substitute_2_price, substitute_2_url, substitution_reasoning.",
        "Find substitutes for this out-of-stock item — Product: {product_name}, Price: ${product_price}, Category: {product_category}, Attributes: {product_attributes}",
        [
          { field: "oos_product_name", as: "oos_product_name", type: "string" },
          { field: "substitute_1_name", as: "sub1_name", type: "string" },
          { field: "substitute_1_price", as: "sub1_price", type: "string" },
          { field: "substitute_1_url", as: "sub1_url", type: "string" },
          { field: "substitute_2_name", as: "sub2_name", type: "string" },
          { field: "substitute_2_price", as: "sub2_price", type: "string" },
          {
            field: "substitution_reasoning",
            as: "substitution_reasoning",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Match substitutes to customer preferences and price tolerance",
        520,
        {
          codeDetails: {
            code: "const originalPrice = parseFloat('{product_price}'); const sub1Price = parseFloat('{sub1_price}'); const sub2Price = parseFloat('{sub2_price}'); const priceTolerance = originalPrice * 0.15; const sub1InRange = Math.abs(sub1Price - originalPrice) <= priceTolerance; const sub2InRange = Math.abs(sub2Price - originalPrice) <= priceTolerance; return { best_match: sub1InRange ? '{sub1_name}' : '{sub2_name}', has_good_match: sub1InRange || sub2InRange, price_match: sub1InRange ? 'within_range' : 'above_range' };",
          },
        },
      ),
      emailNode(
        "4",
        "Email: Present substitution options to customer",
        760,
        "{customer_email}",
        "Update on your order {order_name} — We have alternatives for you",
        "Hi {customer_name},\n\nWe're sorry, but {oos_product_name} from your order {order_name} is currently unavailable.\n\nWe've selected some great alternatives for you:\n\nOption 1: {sub1_name} — ${sub1_price}\n{sub1_url}\n\nOption 2: {sub2_name} — ${sub2_price}\n{sub2_url}\n\n{substitution_reasoning}\n\nReply with your choice, or let us know if you'd prefer a full refund for this item. The rest of your order will ship as planned.\n\nSorry for the inconvenience!\n{store_name} Team",
      ),
      n("5", "delayAction", "Wait 24 hours for customer decision", 1000, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("6", "condition", "Check: Customer accepted a substitution?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{substitution_accepted}",
              operator: "==",
              value: "true",
            },
          ],
        },
      }),
      n("7", "hitApi", "Shopify: Update order with substituted item", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "order",
              value:
                '{"note":"Substitution accepted: {oos_product_name} → {selected_substitute}","tags":"substitution-accepted"}',
            },
          ],
          variables: [
            {
              name: "order.id",
              customName: "updated_order_id",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Notify fulfillment about substitution decision",
        1720,
        "SUBSTITUTION UPDATE — Order {order_name}\n\nOriginal item: {oos_product_name} (OOS)\nCustomer decision: {substitution_accepted ? 'Accepted substitute' : 'Refund requested'}\nSelected: {selected_substitute}\n\nPlease update fulfillment accordingly.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Detects out-of-stock items during fulfillment, uses AI to find the best product substitutes matching price and attributes, emails customers with alternatives, waits 24 hours for their decision, updates the Shopify order with the accepted substitute, and notifies fulfillment on Slack.",
  },

  // ─── 268. Order Status SMS Bot ────────────────────────────────────────

  "Order Status SMS Bot": {
    nodes: [
      trigger(
        "Twilio webhook: Inbound SMS order status inquiry",
        "webhook_app",
        {
          source: "twilio",
          event: "incoming_message",
          howItStarts:
            "Fires when a customer sends an SMS to your Twilio number asking about their order status, sending the phone number, message body, and timestamp.",
          setup:
            "Configure a Twilio phone number with a webhook URL pointing to this workflow for incoming messages. Set the HTTP method to POST. The workflow expects messages containing order numbers or tracking inquiries.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract order reference from SMS message",
        280,
        "You are a customer service SMS parser. Extract the order number or reference from the customer's SMS message. The message may contain variations like 'order #1234', 'wheres my order 1234', 'status of 1234', or just a number. Return order_ref, intent (status_check, tracking, return, complaint, other), customer_name_if_mentioned, is_valid_order_ref.",
        "Parse this customer SMS for order information: {sms_body}. From number: {from_phone}",
        [
          { field: "order_ref", as: "order_ref", type: "string" },
          { field: "intent", as: "customer_intent", type: "string" },
          { field: "is_valid_order_ref", as: "is_valid_ref", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Shopify: Fetch order status and tracking information",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json?name={order_ref}&fields=name,financial_status,fulfillment_status,fulfillments,created_at,total_price",
            headers: [
              {
                key: "X-Shopify-Access-Token",
                value: "{shopify_access_token}",
              },
            ],
            body: [],
            variables: [
              {
                name: "orders[0].fulfillment_status",
                customName: "fulfillment_status",
                type: "string",
              },
              {
                name: "orders[0].financial_status",
                customName: "payment_status",
                type: "string",
              },
              {
                name: "orders[0].fulfillments[0].tracking_number",
                customName: "tracking_number",
                type: "string",
              },
              {
                name: "orders[0].fulfillments[0].tracking_url",
                customName: "tracking_url",
                type: "string",
              },
              {
                name: "orders[0].total_price",
                customName: "order_total",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "runCode", "Format concise SMS response with order status", 760, {
        codeDetails: {
          code: "const status = '{fulfillment_status}' || 'processing'; const tracking = '{tracking_number}'; const hasTracking = tracking && tracking !== 'undefined'; const statusMap = { fulfilled: 'Shipped', partial: 'Partially shipped', null: 'Processing', unfulfilled: 'Processing' }; const friendlyStatus = statusMap[status] || 'Processing'; const smsResponse = hasTracking ? `Order {order_ref}: ${friendlyStatus}. Track: {tracking_url}` : `Order {order_ref}: ${friendlyStatus}. We'll text you when it ships!`; return { sms_response: smsResponse, friendly_status: friendlyStatus, has_tracking: hasTracking };",
        },
      }),
      smsNode(
        "5",
        "Twilio: Reply with order status and tracking info",
        1000,
        "{from_phone}",
        "{sms_response}",
      ),
      n("6", "condition", "Check: Any issue detected with the order?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{customer_intent}",
              operator: "==",
              value: "complaint",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Escalate order issues flagged via SMS",
        1480,
        "SMS ESCALATION\n\nCustomer phone: {from_phone}\nOrder: {order_ref}\nIntent: {customer_intent}\nStatus: {friendly_status}\n\nOriginal message: {sms_body}\n\nPlease follow up with this customer.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log SMS inquiry for support analytics",
        1720,
        "SMSInquiries",
        '[["{from_phone}","{order_ref}","{customer_intent}","{friendly_status}","{has_tracking}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Receives inbound SMS order inquiries via Twilio, uses AI to parse the order reference and intent, looks up real-time order status in Shopify, replies via SMS with status and tracking info, escalates complaints to Slack for human follow-up, and logs all inquiries to Google Sheets.",
  },
};
